/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import dayjs from 'dayjs';
import { Observable, of, throwError, timer } from 'rxjs';
import * as appStrings from '@touchplan/strings';
import { Injectable } from '@angular/core';
import { ILoggerService } from '@touchplan/logging';
import { catchError, filter, map, mergeMap, retryWhen, switchMap, take } from 'rxjs/operators';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import { AppState } from 'src/app/services/app-state.service';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { IAccessCodeData, ITestResult } from 'src/app/common/rally-point/rally-types';
import { AccountCreationStore } from 'src/app/common/auth/services/account-creation-store.service';
import { ActivatedRoute, UrlTree, createUrlTreeFromSnapshot } from '@angular/router';
import { ProjectFirebaseCloudFunctionsService } from './firebase-cloud-functions/project-cloud-functions.service';

import firebase from 'firebase/compat/app';

const baseRoute = 'auth/rally';

@Injectable({
	providedIn: 'root',
})
export class RallyPointService {
	private logger: ILoggerService;

	public getAccessCode$(accessCode: string): Observable<IAccessCodeData> {
		const codePathRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			this.getCodePath(accessCode)
		);
		return this.fireDB
			.object<IAccessCodeData>(codePathRef)
			.valueChanges()
			.pipe(map((codeData) => this.getAccessCodeData(accessCode, codeData)));
	}

	public checkForProjectAccess$(projectId: string, userId: string): Observable<boolean> {
		const path = fancyFirebaseGetters.getIndicesUsersPath(userId, projectId);
		const projectMemberIndexRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			path,
			projectId
		);
		return this.fireDB
			.object<boolean>(projectMemberIndexRef)
			.valueChanges()
			.pipe(map((isMember) => !!isMember));
	}

	public testAccessCode$(accessCodeData: IAccessCodeData): Observable<ITestResult> {
		if (!accessCodeData) {
			return of({ message: appStrings.RALLY_POINT_CODE_MUST_PROVIDED });
		}
		if (!accessCodeData._isValid) {
			return of({ message: accessCodeData.validityMessage });
		}
		if (!this.appState.userSync) {
			return of({ route: [baseRoute, 'login'] });
		}
		return this.grantProjectUserAccess$(accessCodeData.projectId).pipe(
			take(1),
			switchMap(() => this.checkProjectAccessWithRetry$(accessCodeData.projectId)),
			map(() => ({ route: ['plan', accessCodeData.projectId, accessCodeData.planId] }))
		);
	}

	public testAccessCodeAndGetURLTree$(accessCodeData: IAccessCodeData): Observable<UrlTree | null> {
		return this.testAccessCode$(accessCodeData).pipe(
			map((testResult) => {
				if (testResult?.route) {
					testResult.route[0] = '/plan';
					return createUrlTreeFromSnapshot(this.route.snapshot, testResult.route);
				}
				return null;
			})
		);
	}

	public getAccessCodeRedirectURL$(accessCodeData: IAccessCodeData): Observable<ITestResult> {
		return this.testAccessCode$(accessCodeData);
	}

	private grantProjectUserAccess$(projectId: string): Observable<boolean> {
		const userId = this.appState.userSync.$id;
		return this.checkForProjectAccess$(projectId, userId).pipe(
			switchMap((hasAccess) => {
				if (hasAccess) {
					return of(true);
				}
				return this.accountCreationStore.accessCode$.pipe(
					switchMap((accessCode) =>
						this.projectFBCloudFunctions.addUserToProjectViaRallyPoint(userId, accessCode)
					),
					map((response) => !!response.planId)
				);
			}),
			catchError((err) => {
				this.logger.error(err);
				throw new Error(`Failed to grant access to userId: ${userId}, to project: ${projectId}`);
			})
		);
	}

	private getCodePath(code: string): string {
		return fancyFirebaseGetters.getAccessCodesPath(code);
	}

	private getAccessCodeData(codeId: string, code: IAccessCodeData): IAccessCodeData {
		return {
			$id: codeId,
			...code,
			...this.checkCodeValid(code?.timestamp),
		};
	}

	private checkCodeValid(timestamp: number): { _isValid: boolean; validityMessage?: string } {
		if (!timestamp) {
			return {
				_isValid: false,
				validityMessage: appStrings.RALLY_POINT_CODE_INVALID,
			};
		} else if (dayjs(timestamp).isBefore(fbdbTime.syncMoment.valueOf())) {
			return {
				_isValid: false,
				validityMessage: appStrings.RALLY_POINT_CODE_EXPIRED,
			};
		}
		return {
			_isValid: true,
		};
	}

	/**
	 * Checks if the user has access to the projectId provided, it implements a retry strategy to check again in case the user data is outdated.
	 * Retry strategy deatils: retries every 500ms (5s), stops retrying after the user is found to have access to the project or the default maxAttemptsNumber is reached (3)
	 * The maxAttemptsNumber can be changed setting the second parameter (is most be a number).
	 *
	 * @returns a boolean Observable
	 */
	private checkProjectAccessWithRetry$(
		projectId: string,
		maxAttemptsNumber: number = 3
	): Observable<boolean> {
		return this.appState.user.pipe(
			filter((user) => !!user),
			map((user) => {
				// The user indices map might be defined but still the queried project might be missing,
				// in that case want to throw an error for the retryWhen to catch it or pass it
				if (!user.indices?.has(projectId)) {
					throw new Error(appStrings.PROJECT_ACCESS_OUT_OF_DATE_DATA);
				}
				return user.indices.has(projectId);
			}),
			retryWhen((errors) =>
				errors.pipe(
					mergeMap((error, i) => {
						// In some cases the user data might be outdated, in which case we want to retry getting new info
						// Supporting 3 times by default
						if (i >= maxAttemptsNumber) {
							return throwError(error);
						}
						// On first 3 errors we retry after a delay of 0.5s between each
						return timer(500);
					})
				)
			),
			catchError((error) => {
				// Catching the error, loggin it and mapping to a false
				this.logger.error(error);
				return of(false);
			}),
			take(1)
		);
	}

	constructor(
		private appState: AppState,
		private fireDB: AngularFireDatabase,
		private projectFBCloudFunctions: ProjectFirebaseCloudFunctionsService,
		private accountCreationStore: AccountCreationStore,
		private route: ActivatedRoute
	) {}
}
